import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Slice } from '@furality/furality-purpose';
import useAgeVerification from '#/vite/hooks/useAgeVerification.ts';
import VrchatAgeVerificationModal from '@/components/_age-verification/VrchatAgeVerificationModal.tsx';
import YotiAgeVerificationModal from '@/components/_age-verification/YotiAgeVerificationModal.tsx';
import InfoCard from '@/components/_status/InfoCard.tsx';
import { FullLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading';
import { useModalToggler } from '@/lib/helpers/hash.ts';
import useFlag from '@/lib/hooks/useFlag';

const AgeVerification = () => {
  const { t } = useTranslation(['common', 'age-verification']);

  const { isOver18, ageVerificationProvider, verificationStatus } = useAgeVerification();

  const yotiAgeVerificationEnabled = useFlag('ageverification.yoti');
  const vrchatAgeVerificationEnabled = useFlag('ageverification.vrchat');
  const isAgeVerificationEnabled = vrchatAgeVerificationEnabled || yotiAgeVerificationEnabled;

  const [yotiModal, setYotiModal] = useModalToggler('yoti');
  const [vrchatModal, setVrchatModal] = useModalToggler('vrchat');

  const doVrchatVerification = () => {
    setVrchatModal(true);
  };

  const doYotiVerification = () => {
    setYotiModal(true);
  };

  if (!verificationStatus.isFetched) {
    return <PageLoading />;
  }

  return (
    <>
      <Slice size="lg">
        <h1>{t('shared.navigation.age_verification_instructions')}</h1>
        <Card>
          <Card.Body>
            <Trans
              i18nKey="content.information"
              ns="age-verification"
              components={{
                p: <p />,
                LearnMoreLink: <a href="https://furality.org/ageverification" />,
              }}
            />
          </Card.Body>
        </Card>
        {!isAgeVerificationEnabled && (
          <Container>
            <InfoCard>{t('age-verification:content.verification_disabled')}</InfoCard>
          </Container>
        )}
        {isOver18 ? (
          <Container>
            <InfoCard>
              <Trans
                i18nKey="content.already_verified_with"
                ns="age-verification"
                values={{ ageVerificationProvider }}
              />
            </InfoCard>
          </Container>
        ) : (
          <Container>
            <Row className="justify-content-center">
              {vrchatAgeVerificationEnabled && (
                <Col xs="auto">
                  <Button size="xl" onClick={doVrchatVerification}>
                    {t('age-verification:button.verify.vrchat')}
                  </Button>
                </Col>
              )}
              {yotiAgeVerificationEnabled && (
                <Col xs="auto">
                  <Button size="xl" onClick={doYotiVerification}>
                    {t('age-verification:button.verify.yoti')}
                  </Button>
                </Col>
              )}
            </Row>
          </Container>
        )}
      </Slice>
      <VrchatAgeVerificationModal show={vrchatModal} onClose={() => setVrchatModal(false)} />
      <YotiAgeVerificationModal show={yotiModal} onClose={() => setYotiModal(false)} />
    </>
  );
};

export const Route = createFileRoute('/age-verification')({
  component: () => (
    <FullLayout title="shared.navigation.age_verification_instructions">
      <AgeVerification />
    </FullLayout>
  ),
});
